/**
 * Contract entities ⇄ domain values, with the same defaults as ContractCodec.swift.
 */
import { dec, type Decimal } from '../core/decimal.ts';
import { defaultTaxRelief } from '../models/tax-relief.ts';
import type * as Wire from '../types/contract.generated.ts';
import type { Day } from '../core/calendar.ts';
import type {
  Asset, AssetSaleEvent, BalanceSnapshot, Bucket, Checkpoint, DatedChange, DepreciatingAsset, DepreciationRates, Frequency,
  Goal, Plan, Property, RateChange, Series, Transaction, ValueTrajectory, Window,
} from '../types/domain.ts';
import {
  decodeDay, decodeDecimal, encodeDay, encodeDecimal, optional, optionalDay, optionalDecimal, orDefault,
} from './scalars.ts';

export const decodeFrequency = (value: Wire.Frequency | null | undefined): Frequency => orDefault(value, 'monthly');

export const decodeRateChange = (value: Wire.RateChange): RateChange => ({
  date: decodeDay(value.date),
  newRate: decodeDecimal(value.newRate),
});

const list = <T, R>(value: readonly T[] | null | undefined, decode: (item: T) => R): readonly R[] =>
  (value ?? []).map(decode);

export function decodeTransaction(t: Wire.Transaction): Transaction {
  return {
    id: t.id,
    label: orDefault(t.label, t.id),
    type: t.type,
    amount: decodeDecimal(t.amount),
    frequency: decodeFrequency(t.frequency),
    startDate: decodeDay(t.startDate),
    endDate: optionalDay(t.endDate),
    endAfterOccurrences: orDefault(t.endAfterOccurrences, null),
    annualIncreasePercent: optionalDecimal(t.annualIncreasePercent) ?? dec(0),
    isActive: orDefault(t.isActive, true),
    paymentDayOfMonth: orDefault(t.paymentDayOfMonth, null),
    linkedAssetId: orDefault(t.linkedAssetId, null),
    interestRate: optionalDecimal(t.interestRate),
    initialBalance: optionalDecimal(t.initialBalance),
    employerMatch: optionalDecimal(t.employerMatch),
    employerMatchIncomeId: orDefault(t.employerMatchIncomeId, null),
    taxRelief: optional(t.taxRelief, (relief) => ({
      accountType: relief.accountType,
      reliefPercentage: optionalDecimal(relief.reliefPercentage) ?? defaultTaxRelief(relief.accountType),
    })),
    debtType: orDefault(t.debtType, null),
    debtEntryMode: orDefault(t.debtEntryMode, null),
    debtPrincipal: optionalDecimal(t.debtPrincipal),
    debtCurrentBalance: optionalDecimal(t.debtCurrentBalance),
    debtBalanceDate: optionalDay(t.debtBalanceDate),
    debtTermMonths: orDefault(t.debtTermMonths, null),
    debtRemainingMonths: orDefault(t.debtRemainingMonths, null),
    debtStartDate: optionalDay(t.debtStartDate),
    debtIsInterestOnly: orDefault(t.debtIsInterestOnly, null),
    interestRateChanges: optional(t.interestRateChanges, (changes) => changes.map(decodeRateChange)),
    ...(t.history && t.history.length > 0 ? { history: t.history.map(decodeDatedChange).sort((a, b) => a.from - b.from) } : {}),
  };
}

export function decodeDatedChange(change: Wire.DatedChange): DatedChange {
  const { amount, annualIncreasePercent, interestRate, employerMatch } = change.was;
  return {
    from: decodeDay(change.from),
    was: {
      ...(amount === undefined ? {} : { amount: decodeDecimal(amount) }),
      ...(annualIncreasePercent === undefined ? {} : { annualIncreasePercent: decodeDecimal(annualIncreasePercent) }),
      ...(interestRate === undefined ? {} : { interestRate: optionalDecimal(interestRate) }),
      ...(employerMatch === undefined ? {} : { employerMatch: optionalDecimal(employerMatch) }),
    },
  };
}

export const decodeTransactions = (value: readonly Wire.Transaction[] | null | undefined): readonly Transaction[] =>
  list(value, decodeTransaction);

const decodeRates = (value: Wire.DepreciationRates | null | undefined): DepreciationRates | null =>
  optional(value, (r) => ({ year1: decodeDecimal(r.year1), year2: decodeDecimal(r.year2), year3Plus: decodeDecimal(r.year3Plus) }));

function decodeTrajectory(value: Wire.ValueTrajectory): ValueTrajectory {
  switch (value.kind) {
    case 'appreciating':
      return { kind: 'appreciating', annualRate: decodeDecimal(value.annualRate) };
    case 'depreciating':
      return { kind: 'depreciating', preset: value.preset, custom: decodeRates(value.custom) };
    case 'stable':
      return { kind: 'stable' };
  }
}

const decodeAssetSale = (value: Wire.AssetSaleEvent | null | undefined): AssetSaleEvent | null =>
  optional(value, (s) => ({
    date: decodeDay(s.date),
    expectedPrice: optionalDecimal(s.expectedPrice),
    sellingCostsPercent: optionalDecimal(s.sellingCostsPercent) ?? dec(0),
  }));

export const decodeAsset = (a: Wire.Asset): Asset => ({
  id: a.id,
  label: orDefault(a.label, a.id),
  isActive: orDefault(a.isActive, true),
  purchasePrice: decodeDecimal(a.purchasePrice),
  purchaseDate: decodeDay(a.purchaseDate),
  assetClass: a.assetClass,
  valueTrajectory: decodeTrajectory(a.valueTrajectory),
  saleEvent: decodeAssetSale(a.saleEvent),
});

const zeroOr = (value: string | null | undefined): Decimal => optionalDecimal(value) ?? dec(0);

export const decodeProperty = (p: Wire.Property): Property => ({
  id: p.id,
  label: orDefault(p.label, p.id),
  isActive: orDefault(p.isActive, true),
  startDate: decodeDay(p.startDate),
  propertyValue: decodeDecimal(p.propertyValue),
  annualAppreciationRate: optionalDecimal(p.annualAppreciationRate) ?? dec(3),
  mortgage: {
    loanAmount: decodeDecimal(p.mortgage.loanAmount),
    monthlyPayment: optionalDecimal(p.mortgage.monthlyPayment),
    interestRate: decodeDecimal(p.mortgage.interestRate),
    termYears: orDefault(p.mortgage.termYears, 25),
    startDate: decodeDay(p.mortgage.startDate),
    isInterestOnly: orDefault(p.mortgage.isInterestOnly, false),
    rateChanges: list(p.mortgage.rateChanges, decodeRateChange),
  },
  monthlyCosts: optionalDecimal(p.monthlyCosts),
  costBreakdown: optional(p.costBreakdown, (b) => ({
    councilTax: zeroOr(b.councilTax),
    insurance: zeroOr(b.insurance),
    maintenance: zeroOr(b.maintenance),
    groundRent: zeroOr(b.groundRent),
    serviceCharge: zeroOr(b.serviceCharge),
  })),
  saleEvent: optional(p.saleEvent, (s) => ({
    date: decodeDay(s.date),
    salePrice: optionalDecimal(s.salePrice),
    sellingCostsPercent: optionalDecimal(s.sellingCostsPercent) ?? dec('2.5'),
  })),
});

export const decodeDepreciatingAsset = (a: Wire.DepreciatingAsset): DepreciatingAsset => ({
  id: a.id,
  label: orDefault(a.label, a.id),
  isActive: orDefault(a.isActive, true),
  purchasePrice: decodeDecimal(a.purchasePrice),
  purchaseDate: decodeDay(a.purchaseDate),
  depreciationPreset: orDefault(a.depreciationPreset, 'vehicle-standard'),
  customDepreciation: decodeRates(a.customDepreciation),
  finance: optional(a.finance, (f) => ({
    type: orDefault(f.type, 'owned'),
    monthlyPayment: optionalDecimal(f.monthlyPayment),
    termMonths: orDefault(f.termMonths, null),
    endAction: orDefault(f.endAction, 'keep'),
  })),
  saleEvent: decodeAssetSale(a.saleEvent),
  monthlyRunningCosts: optionalDecimal(a.monthlyRunningCosts),
});

export const decodeGoal = (g: Wire.Goal): Goal => ({
  id: g.id,
  label: orDefault(g.label, g.id),
  series: g.series,
  operation: g.operation,
  targetValue: decodeDecimal(g.targetValue),
  startingValue: zeroOr(g.startingValue),
  priority: orDefault(g.priority, 0),
  ...(g.targetDate === undefined ? {} : { targetDate: decodeDay(g.targetDate) }),
});

/** A debt entered by its balance without a balance day was typed with the setup balances (TIMELINE judgement call 11). */
const withSetupBalanceDay = (t: Transaction, setupDay: Day | null): Transaction =>
  t.debtBalanceDate === null && t.debtEntryMode === 'currentBalance' && setupDay !== null ? { ...t, debtBalanceDate: setupDay } : t;

export const decodePlan = (p: Wire.Plan): Plan => ({
  configuration: {
    initialBalance: decodeDecimal(p.configuration.initialBalance),
    initialBalanceDate: optionalDay(p.configuration.initialBalanceDate),
    timeframeYears: p.configuration.timeframeYears,
    expectedReturnPercent: decodeDecimal(p.configuration.expectedReturnPercent),
    safeWithdrawalRatePercent: decodeDecimal(p.configuration.safeWithdrawalRatePercent),
    taxJurisdiction: orDefault(p.configuration.taxJurisdiction, 'none'),
  },
  transactions: decodeTransactions(p.transactions).map((t) => withSetupBalanceDay(t, optionalDay(p.configuration.initialBalanceDate))),
  assets: list(p.assets, decodeAsset),
  properties: list(p.properties, decodeProperty),
  depreciatingAssets: list(p.depreciatingAssets, decodeDepreciatingAsset),
  goals: list(p.goals, decodeGoal),
});

export const decodeBalances = (b: Wire.Balances | null | undefined): BalanceSnapshot => ({
  cash: zeroOr(b?.cash),
  investments: zeroOr(b?.investments),
  savings: zeroOr(b?.savings),
  debt: zeroOr(b?.debt),
  propertyEquity: zeroOr(b?.propertyEquity),
  assetValue: optionalDecimal(b?.assetValue),
});

export const decodeCheckpoint = (c: Wire.Checkpoint): Checkpoint => ({
  id: orDefault(c.id, 'checkpoint'),
  date: decodeDay(c.date),
  confirmedBalances: decodeBalances(c.confirmedBalances),
  forecastedBalances: decodeBalances(c.forecastedBalances),
});

// MARK: Windows and series

export const decodeWindow = (w: Wire.Window): Window => ({
  start: decodeDay(w.start),
  end: decodeDay(w.end),
  interval: orDefault(w.interval, 'month'),
});

export const encodeWindow = (w: Window): Wire.Window => ({
  start: encodeDay(w.start),
  end: encodeDay(w.end),
  interval: w.interval,
});

/** Bucket i spans [starts[i], starts[i+1]); the last spans [starts[n-1], end). */
export function decodeSeries(s: Wire.Series | null | undefined): Series {
  if (!s || s.starts.length === 0) return [];
  if (s.starts.length !== s.amounts.length) throw new Error('series starts/amounts length mismatch');
  if (s.end === null) throw new Error('series with buckets needs an end date');
  const starts = s.starts.map(decodeDay);
  const end = decodeDay(s.end);
  return starts.map((start, i): Bucket => ({
    start,
    end: starts[i + 1] ?? end,
    amount: decodeDecimal(s.amounts[i]!),
  }));
}

export const encodeSeries = (buckets: Series): Wire.Series => ({
  starts: buckets.map((b) => encodeDay(b.start)),
  end: buckets.length > 0 ? encodeDay(buckets[buckets.length - 1]!.end) : null,
  amounts: buckets.map((b) => encodeDecimal(b.amount)),
});

export const encodeAmounts = (buckets: Series): readonly string[] => buckets.map((b) => encodeDecimal(b.amount));

/**
 * ContractOutput.group: series on identical buckets share one `starts`/`end` and list their
 * amounts by name; otherwise each is written as a full series under `series`.
 */
export function encodeBucketGroup(named: readonly (readonly [string, Series])[]): Wire.BucketGroup {
  const reference = named[0]?.[1] ?? [];
  const aligned = named.every(
    ([, buckets]) =>
      buckets.length === reference.length &&
      buckets.every((b, i) => b.start === reference[i]!.start && b.end === reference[i]!.end),
  );
  if (!aligned) return { series: Object.fromEntries(named.map(([name, buckets]) => [name, encodeSeries(buckets)])) };
  return {
    starts: reference.map((b) => encodeDay(b.start)),
    end: reference.length > 0 ? encodeDay(reference[reference.length - 1]!.end) : null,
    ...Object.fromEntries(named.map(([name, buckets]) => [name, encodeAmounts(buckets)])),
  };
}
